import { createLogger, Logger } from '@orchard9ai/logging';
import { ErrorCode } from '@orchard9ai/types';
import { 
  ErrorSeverity, 
  ErrorCategory 
} from '../types/index.js';
import type { 
  ApiError, 
  ErrorContext, 
  DisplayError, 
  ErrorHandlerConfig
} from '../types/index.js';

/**
 * Main error handler class that processes API errors and integrates with logging
 */
export class ErrorHandler {
  private logger: Logger;
  private config: ErrorHandlerConfig;

  constructor(config: Partial<ErrorHandlerConfig> = {}) {
    this.config = {
      enableLogging: true,
      enableMonitoring: true,
      defaultMessages: this.getDefaultMessages(),
      transformers: {},
      loggerName: 'error-handler',
      ...config
    };

    this.logger = createLogger(this.config.loggerName);
  }

  /**
   * Handle API error from go-core-http-toolkit format
   */
  handleApiError(error: ApiError, context: ErrorContext = {}): DisplayError {
    const severity = this.determineSeverity(error);
    const category = this.categorizeError(error);

    // Log the error if enabled
    if (this.config.enableLogging) {
      this.logError(error, context, severity, category);
    }

    // Transform for display
    return this.transformForDisplay(error);
  }

  /**
   * Handle network errors
   */
  handleNetworkError(error: Error, context: ErrorContext = {}): DisplayError {
    const apiError: ApiError = {
      error: 'Network connection failed',
      code: 'NETWORK_ERROR',
      details: {
        message: error.message,
        name: error.name
      }
    };

    return this.handleApiError(apiError, context);
  }

  /**
   * Handle validation errors
   */
  handleValidationError(fields: Record<string, string>, context: ErrorContext = {}): DisplayError {
    const apiError: ApiError = {
      error: 'Validation failed',
      code: 'VALIDATION_ERROR',
      details: fields
    };

    return this.handleApiError(apiError, context);
  }

  /**
   * Transform API error to display-friendly format
   */
  transformForDisplay(error: ApiError): DisplayError {
    // Check for custom transformer
    if (error.code && this.config.transformers && this.config.transformers[error.code]) {
      return this.config.transformers[error.code]!(error);
    }

    // Default transformation
    const type = this.getDisplayType(error);
    const retryable = this.isRetryable(error);
    
    return {
      title: this.getDisplayTitle(error),
      message: error.error,
      type,
      retryable,
      fieldErrors: error.details || {},
      actions: this.getErrorActions(error, retryable)
    };
  }

  /**
   * Register custom error transformer
   */
  registerTransformer(code: string, transformer: (error: ApiError) => DisplayError): void {
    this.config.transformers![code] = transformer;
  }

  /**
   * Check if error is retryable
   */
  private isRetryable(error: ApiError): boolean {
    const retryableCodes = [
      'NETWORK_ERROR',
      'TIMEOUT',
      'SERVICE_UNAVAILABLE',
      'INTERNAL_ERROR',
      'RATE_LIMIT_EXCEEDED'
    ];

    return retryableCodes.includes(error.code || '');
  }

  /**
   * Determine error severity
   */
  private determineSeverity(error: ApiError): ErrorSeverity {
    if (!error.code) return ErrorSeverity.Medium;

    const criticalCodes = ['INTERNAL_ERROR', 'SERVICE_UNAVAILABLE'];
    const highCodes = ['UNAUTHORIZED', 'FORBIDDEN', 'NOT_FOUND'];
    const lowCodes = ['VALIDATION_ERROR', 'INVALID_INPUT'];

    if (criticalCodes.includes(error.code)) return ErrorSeverity.Critical;
    if (highCodes.includes(error.code)) return ErrorSeverity.High;
    if (lowCodes.includes(error.code)) return ErrorSeverity.Low;

    return ErrorSeverity.Medium;
  }

  /**
   * Categorize error by type
   */
  private categorizeError(error: ApiError): ErrorCategory {
    if (!error.code) return ErrorCategory.Unknown;

    const authCodes = ['UNAUTHORIZED', 'FORBIDDEN', 'TOKEN_EXPIRED', 'TOKEN_INVALID'];
    const validationCodes = ['VALIDATION_ERROR', 'INVALID_INPUT', 'MISSING_REQUIRED_FIELD'];
    const networkCodes = ['NETWORK_ERROR', 'TIMEOUT', 'SERVICE_UNAVAILABLE'];
    const systemCodes = ['INTERNAL_ERROR', 'CONFIG_ERROR'];

    if (authCodes.includes(error.code)) return ErrorCategory.Auth;
    if (validationCodes.includes(error.code)) return ErrorCategory.Validation;
    if (networkCodes.includes(error.code)) return ErrorCategory.Network;
    if (systemCodes.includes(error.code)) return ErrorCategory.System;

    return ErrorCategory.Business;
  }

  /**
   * Log error with appropriate level
   */
  private logError(
    error: ApiError, 
    context: ErrorContext, 
    severity: ErrorSeverity, 
    category: ErrorCategory
  ): void {
    const logData = {
      error: error.error,
      code: error.code,
      details: error.details,
      trace_id: error.trace_id,
      category,
      ...context
    };

    switch (severity) {
      case ErrorSeverity.Critical:
        this.logger.error('Critical error occurred', logData);
        break;
      case ErrorSeverity.High:
        this.logger.error('High severity error', logData);
        break;
      case ErrorSeverity.Medium:
        this.logger.warn('Medium severity error', logData);
        break;
      case ErrorSeverity.Low:
        this.logger.info('Low severity error', logData);
        break;
    }
  }

  /**
   * Get display type from error
   */
  private getDisplayType(error: ApiError): 'error' | 'warning' | 'info' {
    const severity = this.determineSeverity(error);
    
    if (severity === ErrorSeverity.Critical || severity === ErrorSeverity.High) {
      return 'error';
    }
    if (severity === ErrorSeverity.Medium) {
      return 'warning';
    }
    return 'info';
  }

  /**
   * Get display title from error
   */
  private getDisplayTitle(error: ApiError): string {
    if (error.code && this.config.defaultMessages && this.config.defaultMessages[error.code]) {
      return this.config.defaultMessages[error.code]!;
    }

    // Generate title from code
    if (error.code) {
      return error.code
        .toLowerCase()
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }

    return 'Error';
  }

  /**
   * Get error actions based on error type
   */
  private getErrorActions(error: ApiError, retryable: boolean): Array<{ label: string; handler: () => void; type?: 'primary' | 'secondary' | 'danger' }> {
    const actions: Array<{ label: string; handler: () => void; type?: 'primary' | 'secondary' | 'danger' }> = [];

    if (retryable) {
      actions.push({
        label: 'Retry',
        handler: () => window.location.reload(),
        type: 'primary'
      });
    }

    if (error.code === 'UNAUTHORIZED') {
      actions.push({
        label: 'Sign In',
        handler: () => {
          // Navigate to sign in - apps should override this
          console.log('Navigate to sign in');
        },
        type: 'primary'
      });
    }

    return actions;
  }

  /**
   * Default error messages
   */
  private getDefaultMessages(): Record<string, string> {
    return {
      [ErrorCode.UNAUTHORIZED]: 'Authentication Required',
      [ErrorCode.FORBIDDEN]: 'Access Denied',
      [ErrorCode.NOT_FOUND]: 'Not Found',
      [ErrorCode.VALIDATION_ERROR]: 'Invalid Input',
      [ErrorCode.RATE_LIMIT_EXCEEDED]: 'Too Many Requests',
      [ErrorCode.INTERNAL_ERROR]: 'System Error',
      [ErrorCode.SERVICE_UNAVAILABLE]: 'Service Unavailable',
      [ErrorCode.TIMEOUT]: 'Request Timeout',
      'NETWORK_ERROR': 'Network Error'
    };
  }
}